import React, { useState, useRef, useEffect } from 'react';
import { Container, Box, TextField, Button, Typography, Card, Alert, CircularProgress } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { verifyPartnerOTP, clearError } from '../../redux/partnerSlice';
import { RootState, AppDispatch } from '../../redux/store';
import PublicHeader from '../../components/Layout/PublicHeader';

export const OTPVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, partner } = useSelector((state: RootState) => state.partner);
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  
  // Get phone number from location state (passed from registration)
  const phone = location.state?.phone || partner?.phone || '';

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
    
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = pastedData.split('').concat(Array(6 - pastedData.length).fill(''));
    setOtp(newOtp);

    // Focus last filled input or first empty
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      return;
    }

    const result = await dispatch(verifyPartnerOTP(otpString));
    if (result.type === verifyPartnerOTP.fulfilled.type) {
      navigate('/dashboard');
    }
  };

  const handleResendOTP = async () => {
    try {
      const response = await fetch('http://localhost:3000/api/v1/partners/resend-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        alert(`OTP sent to ${data.phone}. Check backend console for the new OTP.`);
        // Clear current OTP input
        setOtp(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      } else {
        alert('Failed to resend OTP. Please try again.');
      }
    } catch (error) {
      console.error('Resend OTP error:', error);
      alert('Failed to resend OTP. Please try again.');
    }
  };

  const isOtpComplete = otp.every(digit => digit !== '');

  return (
    <>
      <PublicHeader />
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 60px)' }}>
        <Card sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
            Verify Your Phone
          </Typography>

          <Typography variant="body2" sx={{ mb: 4, textAlign: 'center', color: '#666' }}>
            We've sent a 6-digit OTP to
            <br />
            <strong>{phone}</strong>
            <br />
            Check your backend console for the OTP (development mode)
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <form onSubmit={handleSubmit}>
            <Box 
              sx={{ 
                display: 'flex', 
                gap: 1.5, 
                justifyContent: 'center', 
                mb: 3 
              }}
              onPaste={handlePaste}
            >
              {otp.map((digit, index) => (
                <TextField
                  key={index}
                  inputRef={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e as any)}
                  inputProps={{
                    maxLength: 1,
                    style: { 
                      textAlign: 'center', 
                      fontSize: '24px',
                      fontWeight: 'bold',
                      padding: '16px 0'
                    }
                  }}
                  sx={{
                    width: '56px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderWidth: '2px',
                        borderColor: digit ? '#14B8A6' : '#e0e0e0'
                      },
                      '&:hover fieldset': {
                        borderColor: '#14B8A6'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#14B8A6'
                      }
                    }
                  }}
                />
              ))}
            </Box>

            <Button
              fullWidth
              variant="contained"
              sx={{ mt: 2, py: 1.5 }}
              type="submit"
              disabled={!isOtpComplete || isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Verify OTP'}
            </Button>
          </form>

          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#666' }}>
              Didn't receive the code?{' '}
              <Button
                onClick={handleResendOTP}
                sx={{ 
                  textTransform: 'none', 
                  p: 0, 
                  minWidth: 'auto',
                  color: '#14B8A6',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                Resend OTP
              </Button>
            </Typography>
          </Box>
        </Card>
      </Box>
    </Container>
    </>
  );
};

export default OTPVerificationPage;
